import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { randomString } from 'vcs-common';
import { toast } from 'react-toastify';
import ModalWrap from '../modal-wrap';
import { CloseCrossBtn } from '../../icons/close-cross-btn';
import { ArrowLeft } from '../../icons/arrow-left';
import Input from '../input';
import { DatePickerCustom } from '../datepicker';
import { TimePickerCustom } from '../timepicker';
import { CustomSelect } from '../select';
import Button from '../button';
import { UploadAudioInnerIcon } from '../../icons/upload-audio-inner-icon';
import { PlayAudioIcon } from '../../icons/play-audio';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../hooks/redux';
import { useNavigate } from 'react-router-dom';
import { navigateToJournalSession } from '../../../pages/journal/journal-navigation';
import { startPaymentWidget } from '../../../utils/cloudPaumentUtils';
import { getMyTimeZone } from '../../../utils/getTime';
import {
  useCreateClientMutation,
  useCreateConsultationMutation,
  useGetClientsQuery,
} from '../../../services/users.api';
import {
  useCheckOfflineAudioAvailabilityMutation,
  useUpdateOfflineAudioLifecycleMutation,
} from '../../../services/consultations.api';
import {
  useCreateQuoteMutation,
  useGetUploadUrlMutation,
  useLazyGetTranscriptionByIdQuery,
  useStartTranscriptionMutation,
} from '../../../services/consultation-audio.api';
import { useCancelOrderMutation, useCreateTranscriptionCombinedOrderMutation } from '../../../services/billing.api';
import { usePosthogCapture } from '../../../hooks/usePosthogCapture';

const MEETING_FORMAT_OPTIONS = ['Оффлайн'] as const;

function consultationTypeFromFormatLabel(label: string): 'online' | 'offline' {
  return label === 'Онлайн' ? 'online' : 'offline';
}

function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} Мб`;
}

function addHourToTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + 60;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${nh.toString().padStart(2, '0')}:${nm.toString().padStart(2, '0')}`;
}

function isEndTimeAfterStart(start: string, end: string): boolean {
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  return endMinutes > startMinutes;
}

type UploadAudioModalProps = {
  isOpen: boolean;
  onClose: () => void;
  clientNames?: string[];
};

type PreparedFlow = {
  consultationId: string;
  clientId: number;
  recordingId: string;
  quoteToken: string;
  billableMinutes: number;
  ratePerMinute: number;
  estimatedCost: number;
  currency: string;
  publicOrderId: string;
  payableAmount: number;
  flowId: string;
};

type CreatedConsultationDraft = {
  consultationId: string;
  clientId: number;
  flowId: string;
};

function normalizeMessage(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getErrorMessage(error: unknown): string | null {
  const direct = normalizeMessage(error);
  if (direct) return direct;

  if (error instanceof Error) {
    const message = normalizeMessage(error.message);
    if (message) return message;
  }

  if (typeof error === 'object' && error !== null) {
    const maybeData = (error as { data?: unknown }).data;
    if (typeof maybeData === 'object' && maybeData !== null) {
      const data = maybeData as { message?: unknown; error?: unknown; detail?: unknown };
      return (
        normalizeMessage(data.message) ||
        normalizeMessage(data.error) ||
        normalizeMessage(data.detail)
      );
    }
  }

  return null;
}

const AUDIO_PAYMENT_PLAN_ID = 'audio_processing';

const AUDIO_FILE_ACCEPT = [
  'audio/*',
  '.aac',
  '.ac3',
  '.aif',
  '.aiff',
  '.alac',
  '.amr',
  '.caf',
  '.ec3',
  '.flac',
  '.m4a',
  '.m4b',
  '.mp2',
  '.mp3',
  '.mp4',
  '.oga',
  '.ogg',
  '.opus',
  '.wav',
  '.wave',
  '.webm',
  '.weba',
  '.wma',
  '.3gp',
  '.3gpp',
].join(',');

const AUDIO_MIME_TYPES_BY_EXTENSION: Record<string, string> = {
  aac: 'audio/aac',
  ac3: 'audio/ac3',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
  alac: 'audio/alac',
  amr: 'audio/amr',
  caf: 'audio/x-caf',
  ec3: 'audio/eac3',
  flac: 'audio/flac',
  m4a: 'audio/mp4',
  m4b: 'audio/mp4',
  mp2: 'audio/mpeg',
  mp3: 'audio/mpeg',
  mp4: 'audio/mp4',
  oga: 'audio/ogg',
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  wav: 'audio/wav',
  wave: 'audio/wav',
  weba: 'audio/webm',
  webm: 'audio/webm',
  wma: 'audio/x-ms-wma',
  '3gp': 'audio/3gpp',
  '3gpp': 'audio/3gpp',
};

const getAudioContentType = (file: File) => {
  if (file.type) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  return (extension && AUDIO_MIME_TYPES_BY_EXTENSION[extension]) || 'application/octet-stream';
};

export const UploadAudioModal: FC<UploadAudioModalProps> = ({
  isOpen,
  onClose,
  clientNames = [],
}) => {
  const userData = useAppSelector((state) => state.authSlice.userData);
  const navigate = useNavigate();
  const captureEvent = usePosthogCapture();
  const { data: clients } = useGetClientsQuery({});
  const [createClient] = useCreateClientMutation();
  const [createConsultation] = useCreateConsultationMutation();
  const [checkOfflineAudioAvailability] = useCheckOfflineAudioAvailabilityMutation();
  const [updateOfflineAudioLifecycle] = useUpdateOfflineAudioLifecycleMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();
  const [createQuote] = useCreateQuoteMutation();
  const [createTranscriptionCombinedOrder] = useCreateTranscriptionCombinedOrderMutation();
  const [cancelOrder] = useCancelOrderMutation();
  const [startTranscription] = useStartTranscriptionMutation();
  const [fetchTranscriptionStatus] = useLazyGetTranscriptionByIdQuery();

  const [clientName, setClientName] = useState('');
  const [meetingDate, setMeetingDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [meetingTime, setMeetingTime] = useState('12:00');
  const [meetingTimeEnd, setMeetingTimeEnd] = useState(addHourToTime('12:00'));
  const [meetingFormat, setMeetingFormat] = useState<string>(MEETING_FORMAT_OPTIONS[1]);
  const [file, setFile] = useState<File | null>(null);
  const [hasRecordingConsent, setHasRecordingConsent] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [uploadedDisplay, setUploadedDisplay] = useState<{ name: string; sizeMb: string } | null>(null);
  const [step, setStep] = useState<'form' | 'payment' | 'processing'>('form');
  const [isPreparing, setIsPreparing] = useState(false);
  const [isStartingTranscription, setIsStartingTranscription] = useState(false);
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const [transcriptionStatusText, setTranscriptionStatusText] = useState<string | null>(null);
  const [preparedFlow, setPreparedFlow] = useState<PreparedFlow | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setFile(null);
      setHasRecordingConsent(false);
      setUploadedDisplay(null);
      setIsPreviewPlaying(false);
      setIsPreparing(false);
      setIsStartingTranscription(false);
      setIsPollingStatus(false);
      setIsPaymentConfirmed(false);
      setPreparedFlow(null);
      setTranscriptionStatusText(null);
      setMeetingTime('12:00');
      setMeetingTimeEnd(addHourToTime('12:00'));
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    } else {
      previewAudioRef.current?.pause();
      setFile(null);
      setHasRecordingConsent(false);
      setUploadedDisplay(null);
      setIsPreviewPlaying(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
      }
      previewAudioRef.current?.pause();
    };
  }, []);

  const isTimeRangeValid = isEndTimeAfterStart(meetingTime, meetingTimeEnd);
  const canSubmit = Boolean(clientName.trim() && file && hasRecordingConsent && isTimeRangeValid);

  const handleRemoveFile = () => {
    previewAudioRef.current?.pause();
    setFile(null);
    setUploadedDisplay(null);
    setIsPreviewPlaying(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setIsPreviewPlaying(false);
    setUploadedDisplay({
      name: f.name,
      sizeMb: formatFileSize(f.size),
    });
  };

  const handleTogglePreview = async () => {
    const audio = previewAudioRef.current;
    if (!audio || !previewUrl) return;

    if (audio.paused) {
      try {
        await audio.play();
      } catch (error) {
        console.error('offline_audio_preview_play_failed', error);
        setIsPreviewPlaying(false);
        toast.error('Не удалось воспроизвести выбранный аудиофайл');
      }
      return;
    }

    audio.pause();
  };

  const cancelPreparedFlow = async () => {
    if (!preparedFlow) return;

    await cancelOrder({ publicOrderId: preparedFlow.publicOrderId }).unwrap();
    await updateOfflineAudioLifecycle({
      id: Number(preparedFlow.consultationId),
      lifecycle_status: 'cancelled',
    }).unwrap();
  };

  const handleBack = () => {
    void (async () => {
      try {
        await cancelPreparedFlow();
        setPreparedFlow(null);
        setStep('form');
      } catch (error) {
        console.error('offline_audio_explicit_cancel_failed', { flowId: preparedFlow?.flowId, error });
        toast.error('Не удалось отменить черновик. Попробуйте закрыть окно ещё раз.');
      }
    })();
  };

  const openJournalEntry = useCallback(
    (consultationId: string, clientId: number) => {
      onClose();
      navigateToJournalSession(navigate, Number(consultationId), `/api/v1/clients/${clientId}`, 'Транскрипция');
    },
    [navigate, onClose],
  );

  const createOrFindClientAndConsultation = useCallback(async (): Promise<CreatedConsultationDraft | null> => {
    if (!userData?.id) return null;
    const name = clientName.trim();
    if (!name) return null;

    const consultationTz = await getMyTimeZone();
    const existingClient = clients?.find((c) => c.full_name === name);
    let clientId: number | undefined = existingClient?.id;

    if (!clientId) {
      const created = await createClient({
        full_name: name,
        time_zone: consultationTz,
        is_active: true,
        psychologists: [`/api/v1/users/${userData.id}`],
      }).unwrap();
      clientId = created.id;
    }

    const normalizedDate = dayjs(meetingDate).format('YYYY-MM-DD') + 'T00:00:00.000Z';
    const code = randomString();
    const flowId = randomString();

    const consultation = await createConsultation({
      client_entity: `/api/v1/clients/${clientId}`,
      date: normalizedDate,
      datetime_start: meetingTime,
      datetime_end: meetingTimeEnd,
      client_timezone: consultationTz,
      consultation_timezone: consultationTz,
      code,
      link: '',
      lifecycle_status: 'pending_payment',
      is_group: false,
      consultation_type: consultationTypeFromFormatLabel(meetingFormat),
    } as never).unwrap();

    return { consultationId: String(consultation.id), clientId, flowId };
  }, [
    clientName,
    clients,
    createClient,
    createConsultation,
    meetingDate,
    meetingFormat,
    meetingTime,
    meetingTimeEnd,
    userData?.id,
  ]);

  const pollTranscriptionStatus = useCallback(
    (transcriptionId: string) => {
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
      }

      setIsPollingStatus(true);
      setTranscriptionStatusText('Транскрибация запущена. Ожидаем завершения...');

      const poll = async () => {
        try {
          const status = await fetchTranscriptionStatus(transcriptionId).unwrap();
          const normalizedStatus =
            (status.transcriptionStatus || status.status || '').toString().toLowerCase();

          if (normalizedStatus.includes('completed')) {
            setTranscriptionStatusText('Транскрибация завершена. Открываем запись...');
            setIsPollingStatus(false);
            captureEvent('offline_audio_transcription_completed');
            if (pollingTimerRef.current) {
              window.clearInterval(pollingTimerRef.current);
              pollingTimerRef.current = null;
            }
            if (preparedFlow?.consultationId && preparedFlow.clientId) {
              openJournalEntry(preparedFlow.consultationId, preparedFlow.clientId);
            }
            return;
          }

          if (
            normalizedStatus.includes('failed') ||
            normalizedStatus.includes('error') ||
            normalizedStatus.includes('cancel')
          ) {
            setTranscriptionStatusText('Транскрибация завершилась с ошибкой.');
            setIsPollingStatus(false);
            toast.error(getErrorMessage(status) ?? 'Транскрибация завершилась с ошибкой');
            if (pollingTimerRef.current) {
              window.clearInterval(pollingTimerRef.current);
              pollingTimerRef.current = null;
            }
          }
        } catch (error) {
          setTranscriptionStatusText('Не удалось получить статус транскрибации');
          console.error('offline_audio_transcription_poll_error', { transcriptionId });
          toast.error(getErrorMessage(error) ?? 'Не удалось получить статус транскрибации');
          setIsPollingStatus(false);
          if (pollingTimerRef.current) {
            window.clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
          }
        }
      };

      void poll();
      pollingTimerRef.current = window.setInterval(() => {
        void poll();
      }, 7000);
    },
    [captureEvent, fetchTranscriptionStatus, openJournalEntry, preparedFlow?.consultationId],
  );

  const handleSubmit = async () => {
    if (!canSubmit || !file) return;
    if (!isTimeRangeValid) {
      toast.error('Время окончания должно быть позже времени начала');
      return;
    }

    setIsPreparing(true);
    setTranscriptionStatusText(null);

    try {
      const consultationTz = await getMyTimeZone();
      await checkOfflineAudioAvailability({
        date: dayjs(meetingDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
        datetime_start: meetingTime,
        datetime_end: meetingTimeEnd,
        consultation_timezone: consultationTz,
        consultation_type: consultationTypeFromFormatLabel(meetingFormat),
      } as never).unwrap();
      const contentType = getAudioContentType(file);
      const upload = await getUploadUrl({
        fileName: file.name,
        contentType,
        fileSize: file.size,
      }).unwrap();

      captureEvent('offline_audio_upload_started');
      const uploadResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('upload_failed');
      }

      const consultationDraft = await createOrFindClientAndConsultation();
      if (!consultationDraft) {
        throw new Error('consultation_not_created');
      }
      const { consultationId, clientId, flowId } = consultationDraft;

      const quote = await createQuote({
        recordingId: upload.recordingId,
        consultationId,
      }).unwrap();

      const order = await createTranscriptionCombinedOrder({
        quote_token: quote.quoteToken,
        consultation_id: consultationId,
        bonus_amount: 0,
      }).unwrap();

      setPreparedFlow({
        consultationId,
        clientId,
        recordingId: upload.recordingId,
        quoteToken: quote.quoteToken,
        billableMinutes: quote.billableMinutes ?? quote.durationMinutes ?? 0,
        ratePerMinute: quote.ratePerMinute,
        estimatedCost: quote.estimatedCost,
        currency: quote.currency,
        publicOrderId: order.public_order_id,
        payableAmount: order.payable_amount,
        flowId,
      });
      setStep('payment');
    } catch (error) {
      if (error instanceof Error && error.message === 'upload_failed') {
        toast.error('Не удалось загрузить файл в хранилище');
      } else {
        toast.error(getErrorMessage(error) ?? 'Не удалось подготовить оплату для транскрибации');
      }
    } finally {
      setIsPreparing(false);
    }
  };

  const startPaidTranscription = async () => {
    if (!preparedFlow) return;
    setStep('processing');
    setIsStartingTranscription(true);
    try {
      await updateOfflineAudioLifecycle({
        id: Number(preparedFlow.consultationId),
        lifecycle_status: 'processing',
      }).unwrap();
      const start = await startTranscription({
        publicOrderId: preparedFlow.publicOrderId,
        quoteToken: preparedFlow.quoteToken,
        consultationId: preparedFlow.consultationId,
        recordingId: preparedFlow.recordingId,
      }).unwrap();
      captureEvent('offline_audio_transcription_started');
      await updateOfflineAudioLifecycle({
        id: Number(preparedFlow.consultationId),
        lifecycle_status: 'ready',
      }).unwrap();
      setTranscriptionStatusText('Идёт ИИ-анализ записи');
      pollTranscriptionStatus(start.transcriptionId);
    } catch (error) {
      setStep('payment');
      setTranscriptionStatusText('Оплата прошла. Повторите запуск транскрибации — повторная оплата не потребуется.');
      toast.error(getErrorMessage(error) ?? 'Оплата прошла, но запустить транскрибацию не удалось');
    } finally {
      setIsStartingTranscription(false);
    }
  };

  const handlePay = () => {
    if (!userData?.email || !userData?.id || !preparedFlow || isStartingTranscription || isPollingStatus) return;
    if (isPaymentConfirmed) {
      void startPaidTranscription();
      return;
    }

    startPaymentWidget({
      planId: AUDIO_PAYMENT_PLAN_ID,
      userId: userData.id,
      amount: preparedFlow.payableAmount,
      currency: preparedFlow.currency,
      description: 'Оплата обработки аудиозаписи (транскрибация и ИИ-анализ)',
      email: userData.email,
      transactionId: preparedFlow.publicOrderId,
      bonusesUsed: 0,
      onSuccess: () => {
        captureEvent('offline_audio_payment_success', { consultation_id: preparedFlow.consultationId });
        setIsPaymentConfirmed(true);
        void startPaidTranscription();
      },
      onFail: () => {
        toast.info('Оплата не завершена. Вы можете повторить попытку.');
      },
    });
  };

  const handleClose = () => {
    if (preparedFlow && step === 'payment' && !isPaymentConfirmed) {
      void (async () => {
        try {
          await cancelPreparedFlow();
        } catch (error) {
          console.error('offline_audio_explicit_cancel_failed', { flowId: preparedFlow.flowId, error });
        } finally {
          onClose();
        }
      })();
      return;
    }
    onClose();
  };

  const dateDisplay = dayjs(meetingDate, 'YYYY-MM-DD').format('DD.MM.YYYY');
  const paymentAmountText = useMemo(() => preparedFlow?.payableAmount ?? 0, [preparedFlow]);

  if (!isOpen) return null;

  return (
    <ModalWrap isShow={isOpen} className="!items-stretch !p-0 sm:!items-center sm:!p-[15px]">
      <button
        type="button"
        className="absolute inset-0 z-0"
        onClick={handleClose}
        aria-label="Закрыть окно загрузки аудиозаписи"
      />
      <div
        className={`relative z-10 flex w-full min-w-0 max-h-[100dvh] flex-col overflow-y-auto bg-white p-5 shadow-lg sm:max-h-[calc(100dvh-30px)] sm:rounded-[20px] sm:p-10 ${
          step === 'payment' || step === 'processing' ? 'sm:max-w-[600px] gap-6' : 'sm:max-w-[978px] gap-6'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-audio-modal-title"
      >
        <audio
          ref={previewAudioRef}
          src={previewUrl ?? undefined}
          className="hidden"
          preload="metadata"
          onPlay={() => setIsPreviewPlaying(true)}
          onPause={() => setIsPreviewPlaying(false)}
          onEnded={() => setIsPreviewPlaying(false)}
          onError={() => {
            setIsPreviewPlaying(false);
            toast.error('Не удалось декодировать выбранный аудиофайл');
          }}
        />
        <button
          type="button"
          className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full !bg-transparent hover:!bg-transparent focus-visible:ring-2 focus-visible:ring-[#C00000] focus-visible:ring-offset-2 sm:right-5 sm:top-5 [&>svg]:opacity-100 hover:[&>svg]:opacity-100"
          onClick={handleClose}
          aria-label="Закрыть"
        >
          <CloseCrossBtn width={28} height={28} fill="#2C3038" fillBg="transparent" />
        </button>

        {step === 'payment' ? (
          <>
            {!isPaymentConfirmed && (
              <button
                type="button"
                className="absolute left-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full text-[#2C3038] focus-visible:ring-2 focus-visible:ring-[#C00000] focus-visible:ring-offset-2 sm:left-5 sm:top-5"
                onClick={handleBack}
                aria-label="Назад"
              >
                <ArrowLeft width={24} height={24} fill="#2C3038" />
              </button>
            )}
            <div className="flex flex-col items-center gap-6">
              <h2 id="upload-audio-modal-title" className="font-comfortaa font-semibold text-2xl text-[#2C3038] text-center">
                Аудиозапись загружена
              </h2>
              <div className="flex flex-col gap-2 w-full">
                <div className="border border-[#EAEBEA] rounded-2xl p-4 flex flex-col gap-6 bg-white">
                  <div className="flex flex-col gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="font-manrope font-semibold text-base text-[#2C3038]">Имя клиента</span>
                      <span className="min-w-0 break-words font-manrope text-base text-[#2C3038]">{clientName}</span>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <div className="flex items-center gap-2">
                        <span className="font-manrope font-semibold text-base text-[#2C3038]">Дата</span>
                        <span className="font-manrope text-base text-[#2C3038]">{dateDisplay}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-manrope font-semibold text-base text-[#2C3038]">Время</span>
                        <span className="font-manrope text-base text-[#2C3038]">
                          {meetingTime} - {meetingTimeEnd}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="relative flex h-[52px] items-center rounded-[32px] bg-[#F5F6FA] px-4 pr-14 w-full">
                    <span className="font-manrope font-bold text-base text-[#2C3038] flex-1 truncate mr-3">
                      {uploadedDisplay?.name} ({uploadedDisplay?.sizeMb})
                    </span>
                    <button
                      type="button"
                      className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-[#C00000] text-white hover:opacity-90 absolute right-3 top-1/2 -translate-y-1/2"
                      onClick={() => {
                        void handleTogglePreview();
                      }}
                      disabled={!previewUrl}
                      aria-label={isPreviewPlaying ? 'Поставить запись на паузу' : 'Прослушать запись'}
                    >
                      {isPreviewPlaying ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                          <path d="M6 4v12M14 4v12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <PlayAudioIcon />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-2 rounded-[24px] border border-[#C00000] bg-[#F5F6FA] p-2 sm:flex-row sm:items-center sm:gap-3 sm:rounded-[100px] sm:p-1">
                  <div className="self-start rounded-[100px] bg-[#625B71] px-4 py-2 sm:self-auto">
                    <span className="font-manrope font-semibold text-2xl text-white">
                      {preparedFlow?.ratePerMinute ?? 0} ₽/мин.
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-col gap-1 px-2 py-1 sm:flex-1 sm:flex-row sm:items-center sm:gap-4 sm:px-4 sm:py-2">
                    <span className="font-manrope font-semibold text-xl text-[#2C3038] sm:text-2xl">
                      {preparedFlow?.billableMinutes ?? 0} минут
                    </span>
                    <span className="break-words font-manrope text-xl text-[#2C3038] sm:text-2xl">
                      {preparedFlow?.estimatedCost ?? 0} рублей
                    </span>
                  </div>
                </div>
                {transcriptionStatusText && (
                  <div className="rounded-lg bg-[#F5F6FA] px-4 py-3">
                    <p className="font-manrope text-sm text-[#2C3038]">{transcriptionStatusText}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-center">
              <Button
                type="primary"
                className="inline-flex !w-full !rounded-[16px] !border-[#C00000] !bg-[#C00000] !px-4 !py-[15px] font-manrope !text-base font-normal !text-white hover:!border-[#C00000] hover:!bg-[#C00000] hover:!text-white disabled:opacity-50 sm:!w-auto sm:!px-10"
                onClick={handlePay}
                disabled={!userData?.email || !userData?.id || !preparedFlow || isStartingTranscription || isPollingStatus}
              >
                {isStartingTranscription
                  ? 'Запускаем транскрибацию...'
                  : isPaymentConfirmed
                    ? 'Повторить запуск транскрибации'
                    : `Оплатить ${paymentAmountText} рублей`}
              </Button>
            </div>
          </>
        ) : step === 'form' ? (
          <>
            <div className="flex min-w-0 flex-col gap-6 pr-7 sm:pr-8">
              <h2 id="upload-audio-modal-title" className="font-comfortaa font-semibold text-2xl text-[#2C3038]">
                Загрузка аудиозаписи
              </h2>

              {uploadedDisplay ? (
                <div className="flex h-[52px] w-full max-w-full items-center gap-2 rounded-[32px] border border-[#EAEBEA] bg-[#F5F6FA] py-2 pl-4 pr-2 sm:w-fit">
                  <span className="font-manrope font-bold text-base text-[#2C3038] truncate">
                    {uploadedDisplay.name} ({uploadedDisplay.sizeMb})
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="w-10 h-10 flex items-center justify-center rounded-full shrink-0 hover:bg-black/5 text-[#2C3038]"
                    aria-label="Удалить файл"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                      <path
                        d="M12 4L4 12M4 4l8 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <label className="relative flex h-[52px] w-full items-center gap-2 rounded-full border border-[#C00000] bg-[#F5F6FA] px-4 pr-12 shadow-[1px_1px_12px_0px_rgba(9,88,146,0.16)] transition-colors hover:bg-[#ECEFFA] focus-within:ring-2 focus-within:ring-[#C00000] focus-within:ring-offset-2 sm:w-fit">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-[#2C3038]">
                    <path d="M8 3.33v9.34M3.33 8h9.34" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <span className="mr-3 min-w-0 truncate font-manrope text-base font-bold text-[#2C3038]">Загрузить аудиозапись</span>
                  <span className="w-12 h-12 rounded-full bg-[#C00000] flex items-center justify-center shrink-0 absolute right-1 top-1/2 -translate-y-1/2">
                    <UploadAudioInnerIcon />
                  </span>
                  <input
                    type="file"
                    name="audio-file"
                    accept={AUDIO_FILE_ACCEPT}
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                </label>
              )}

              <label className="flex items-start gap-3 w-full cursor-pointer">
                <input
                  type="checkbox"
                  name="client-recording-consent"
                  checked={hasRecordingConsent}
                  onChange={(event) => setHasRecordingConsent(event.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-[#C00000] focus-visible:ring-2 focus-visible:ring-[#C00000] focus-visible:ring-offset-2"
                />
                <span className="font-manrope text-sm leading-snug text-[#2C3038]">
                  Подтверждаю, что получил(а) информированное согласие клиента на аудиозапись сессии.
                </span>
              </label>

              <div className="w-full rounded-lg bg-[#F5F6FA] px-4 py-2 sm:w-fit">
                <p className="font-manrope font-semibold text-lg text-[#111026] leading-snug">
                  Стоимость подготовки, транскрибации и ИИ-анализа аудиозаписи рассчитывается автоматически.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex flex-col gap-3 sm:col-span-2">
                  <label className="flex items-center gap-2">
                    <span className="font-manrope font-semibold text-base text-[#2C3038]">Имя клиента*</span>
                    <span className="text-[#737478]" title="Введите имя или выберите из списка.">ⓘ</span>
                  </label>
                  <p className="text-[16px] text-[#737478] font-manrope">Введите имя или выберите из списка.</p>
                  {clientNames.length > 0 ? (
                    <CustomSelect
                      placeholder="Например, Максим"
                      items={clientNames}
                      selected={clientName}
                      onChange={setClientName}
                      showError={false}
                      classNameInput="!h-14 border border-[#737478] rounded"
                      classNameWrap="[&>div]:!h-14"
                    />
                  ) : (
                    <Input
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Например, Максим"
                      className="!h-14 border border-[#737478] rounded px-4 font-manrope text-base"
                      classNameWrap="!h-14"
                    />
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <label className="font-manrope font-semibold text-base text-[#2C3038]">Формат встречи</label>
                  <p className="text-[16px] text-[#737478] font-manrope invisible select-none">
                    Выберите формат встречи.
                  </p>
                  <CustomSelect
                    placeholder="Оффлайн"
                    items={[...MEETING_FORMAT_OPTIONS]}
                    selected={meetingFormat}
                    onChange={setMeetingFormat}
                    showError={false}
                    classNameInput="!h-14 border border-[#2C3038] rounded"
                    classNameWrap="[&>div]:!h-14"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex flex-col gap-3">
                  <label className="font-manrope font-semibold text-base text-[#2C3038]">Дата встречи*</label>
                  <DatePickerCustom
                    date={meetingDate}
                    setDate={setMeetingDate}
                    className="!h-14 [&>input]:!text-base border border-[#2C3038] rounded px-4"
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <label className="font-manrope font-semibold text-base text-[#2C3038]">Начало</label>
                  <TimePickerCustom
                    value={meetingTime}
                    onTimeChange={(value) => {
                      setMeetingTime(value);
                      if (!isEndTimeAfterStart(value, meetingTimeEnd)) {
                        setMeetingTimeEnd(addHourToTime(value));
                      }
                    }}
                    className="!h-14 border border-[#2C3038] rounded px-4"
                    classNameWrap="!h-14"
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <label className="font-manrope font-semibold text-base text-[#2C3038]">Окончание</label>
                  <TimePickerCustom
                    value={meetingTimeEnd}
                    onTimeChange={setMeetingTimeEnd}
                    className="!h-14 border border-[#2C3038] rounded px-4"
                    classNameWrap="!h-14"
                  />
                </div>
              </div>
              {!isTimeRangeValid && (
                <p className="font-manrope text-sm text-[#C00000]">Время окончания должно быть позже времени начала</p>
              )}

              <p className="font-manrope text-sm text-[#2C3038] leading-snug">
                *Все данные, включая заметки к сессиям и записи, шифруются во время передачи и хранения для
                обеспечения полной безопасности.
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                type="black"
                className="inline-flex !w-full !rounded-[16px] !border-[#C00000] !bg-[#C00000] !px-4 !py-[15px] font-manrope !text-base font-normal !text-white hover:!border-[#C00000] hover:!bg-[#C00000] hover:!text-white disabled:opacity-50 sm:!w-auto sm:!px-10"
                onClick={() => {
                  void handleSubmit();
                }}
                disabled={!canSubmit || isPreparing}
              >
                {isPreparing ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border-2 border-white/50 border-t-white animate-spin" />
                    Анализируем аудио...
                  </span>
                ) : (
                  'Продолжить'
                )}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-6 py-8">
            <div className="h-12 w-12 rounded-full border-4 border-[#E2E8F0] border-t-[#C00000] animate-spin" />
            <h2 id="upload-audio-modal-title" className="font-comfortaa font-semibold text-2xl text-[#2C3038] text-center">Идёт ИИ-анализ записи</h2>
            <p className="font-manrope text-base text-[#2C3038] text-center max-w-[460px]">
              Это может занять несколько минут. После завершения транскрибации откроем вкладку «Транскрипция» в
              терапевтическом журнале.
            </p>
          </div>
        )}
      </div>
    </ModalWrap>
  );
};
